import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import Table from '~components/molecules/Table';
import Pagination from '~components/atoms/Pagination';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';
import {
    CustomerIdTypeEnum,
    EarmarkStatusEnum,
    FundTypeEnum,
    GoldTypeEnum,
    ModuleEnum,
    PermissionsEnum,
    SilverTypeEnum,
    TransactionActionEnum,
    TransactionStatusEnum,
} from '~utils/enums';

import * as Styles from './_customerManagementCustomerTransactionTable.module.scss';
import ActionButton from '~components/atoms/ActionButton';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';
import { TTransactionListing } from '~types/feat/gold';
import dayjs from 'dayjs';
import { TUpdateTransactionStatusReduxParams } from '~types/feat/approval';
import ConfirmReleaseEarmarkModal from '../ConfirmReleaseEarmarkModal';
import { TCreateReleaseEarmarkReduxParams } from '~types/feat/customer';

const CustomerManagementTransactionTable = () => {
    const {
        Misc:
        {
            renderTransactionStatusChip,
            dateTimeFormat,
            renderBinaryStatusChip,
        },
    } = UtilFunctions;

    const dispatch = useAppDispatch();

    const isLoading = useAppSelector(state => state.customer.loadings.getCustomerListing);
    const { index, maxIndex, data: customerTransactions } = useAppSelector(state => state.customer.customerTransactions);
    const customerId = useAppSelector(state => state.customer.customerDetails.customerId);

    useEffect(() => {
        dispatch(combinedActions.customerGetCustomerTransactionsAttempt({ customerId }));
    }, [customerId]);

    const setTransactionProductListingModalIsOpen = (state: boolean) => dispatch(combinedActions.goldSetTransactionProductListingModalIsOpen(state));
    const setSelectedGoldTransaction = (transaction: TTransactionListing) => dispatch(combinedActions.goldSetSelectedTransaction(transaction));

    const setEarmarkReleaseModalIsOpen = (state: boolean) => dispatch(combinedActions.customerSetConfirmUpdateReleaseEarmarkModalIsOpen(state));
    const setReleaseEarmarkDetails = (state: TCreateReleaseEarmarkReduxParams) => dispatch(combinedActions.customerSetReleaseEarmarkState(state));

    const setIndex = (idx: number) => dispatch(combinedActions.customerSetCustomerTransactionsIndex({ index: idx }));

    // if maxIndex is lower then index reset index to 0
    useEffect(() => {
        if (index > maxIndex) {
            setIndex(0);
        }
    }, [maxIndex]);

    const handleOnPrev = () => {
        if (index > 0) setIndex(index - 1);
    };
    const handleOnNext = () => {
        if (index < maxIndex) setIndex(index + 1);
    };
    const handleOnFirst = () => setIndex(0);
    const handleOnLast = () => setIndex(maxIndex >= 0 ? maxIndex : 0);

    const handleViewTransaction = (transaction: TTransactionListing) => {
        setSelectedGoldTransaction(transaction);
        setTransactionProductListingModalIsOpen(true);
    };

    const handleConfirmReleaseEarmark = (transactionId: number) => {
        dispatch(combinedActions.customerUpdateReleaseEarmarkFailure(''));
        setEarmarkReleaseModalIsOpen(true);
        setReleaseEarmarkDetails({ transactionId });
    };

    const tableHeader = [
        'TRANSACTION ID',
        'TRANSACTION TYPE',
        'EARMARK TRANSACTION',
        'PROJECT NAME',
        'STATUS',
        'CUSTOMER NAME',
        'CUSTOMER ID TYPE',
        'CUSTOMER ID NUMBER',
        'CIF NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'BRANCH TELLER NAME',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'ACTION',
    ];

    let prevTransactionId = 0;
    const tableBody = customerTransactions.map(transaction => {
        const {
            transactionId,
            isEarmarkTransaction,
            earmarkEndDate,
            projectName,
            name,
            idType,
            idNo,
            CIFNo,
            branchCode,
            branchName,
            regionName,
            status,
            action,
            fundType,
            goldType,
            silverType,
            createdAt,
            branchTellerName,
            salesPersonnelName,
            referralName,
            amountReleased,
        } = transaction;

        const renderTransactionId = () => {
            if (prevTransactionId !== transactionId){
                prevTransactionId = transactionId;
                return transactionId;
            }

            return '-';
        };

        const renderAction = () => {
            if (!action) return '-';

            return TransactionActionEnum[action];
        };

        const isEarmarkReleased =
            amountReleased === EarmarkStatusEnum.Released;

        const canShowEarmarkButton =
            isEarmarkTransaction === true &&
            dayjs().isBefore(dayjs(earmarkEndDate), 'day') &&
            status !== TransactionStatusEnum.Rejected &&
            status !== TransactionStatusEnum.Cancelled &&
            status !== TransactionStatusEnum.Pending;

        return [
            renderTransactionId(),
            (
                <Text
                    key={transactionId}
                    label={renderAction() || ''}
                    className={Styles['transaction-action-label']}
                    isBold
                />
            ),
            isEarmarkTransaction ? renderBinaryStatusChip(isEarmarkTransaction, 'Yes') : '-',
            projectName ? projectName : '-',
            renderTransactionStatusChip(status, TransactionStatusEnum[status]),
            name,
            idType ? CustomerIdTypeEnum[idType] : '-',
            idNo,
            CIFNo,
            branchCode,
            branchName,
            regionName,
            fundType ? FundTypeEnum[fundType] : '-',
            goldType ? GoldTypeEnum[goldType].split(/(?=[A-Z])/).join(' ') : '',
            silverType ? SilverTypeEnum[silverType].split(/(?=[A-Z])/).join(' ') : '',
            branchTellerName || '-',
            salesPersonnelName || '-',
            referralName || '-',
            dateTimeFormat(createdAt),
            (
                <div
                    key={transactionId}
                    className={Styles['action-button-container']}
                >
                    {canShowEarmarkButton && (
                        <ActionButton
                            testId='customer-transactionDetails-release-earmark-button'
                            label={
                                isEarmarkReleased
                                    ? 'Earmark Released Early'
                                    : 'Release Earmark'
                            }
                            disabled={isEarmarkReleased}
                            onClick={() => handleConfirmReleaseEarmark(transactionId)}
                            className={isEarmarkReleased ? Styles['release-earmark-button'] : undefined}
                        />
                    )}
                    <ActionButton
                        testId='customer-transactionDetails-view-details-button'
                        label='View Details'
                        onClick={() => handleViewTransaction(transaction)}
                    />
                </div>
            ),
        ];
    });

    return (
        <div className={Styles.container}>
            <Text
                label='Customer Transactions'
                isBold
            />

            <Table
                isLoading={isLoading}
                header={tableHeader}
                body={tableBody}
            />

            <Pagination
                currentPage={index}
                maxPage={maxIndex}
                onPrev={handleOnPrev}
                onNext={handleOnNext}
                onFirst={handleOnFirst}
                onLast={handleOnLast}
            />

            <ConfirmReleaseEarmarkModal />
        </div>
    );
};

export default CustomerManagementTransactionTable;
